import { gql } from '@apollo/client'

import type { IReferenceNumbers, IStop, ShipmentStatusEnum } from '../../types/shipment'
import type { TShipmentTrackingSourceTypes } from '../../types/tracking'

/**
 * Query to get a shipment's tracking sources and relevant metadata and location data
 */
export const SHIPMENT_TRACKING_SOURCES = gql`
  query TrackingSources($shipmentID: ID!) {
    trackingSources(input: { shipmentID: $shipmentID }) {
      data {
        __typename
        ... on APISource {
          isActive
          addedAt
          filterReason
          lastLocationFetchedAt
          locations {
            city
            coordinates {
              latitude
              longitude
            }
            countryCode
            locationInfo
            postalCode
            stateCode
            timestamp
          }
          metadata {
            sourceID
            lastLocationData {
              city
              coordinates {
                latitude
                longitude
              }
              countryCode
              locationInfo
              postalCode
              stateCode
              timestamp
              nextStopDetails {
                distanceToNextStop {
                  unit
                  value
                }
                etaToNextStop {
                  date
                  time
                  timezone
                }
                sequence
                stopExternalID
                stopID
                stopType
              }
            }
          }
          source
          sourceDisplayName
          sourceID
          sourceType
        }
        ... on MobileAppSource {
          isActive
          locations {
            city
            coordinates {
              latitude
              longitude
            }
            countryCode
            locationInfo
            postalCode
            stateCode
            timestamp
          }
          metadata {
            deviceID
            deviceInfo {
              androidSDKVersion
              deviceID
              batteryLevel
              deviceManufacturer
              deviceModel
              deviceName
              devicePlatform
              deviceOS
              deviceOSVersion
              isDeviceCharging
              isDeviceVirtual
              iOSVersion
              languageCode
            }
            hasSentMockLocations
            isHistoricalDevice
            isLoggedIn
            isTrackingOn
            lastTrackingStoppedAt
            lastLogin
            lastLogout
            lastSeen
            locationSettings {
              accuracyAuthorization
              authorizationStatus
              locationServicesEnabled
              gpsGeolocationProviderAvailable
              networkGeolocationProviderAvailable
            }
            user {
              id
              name
              photo
              phone
            }
            notificationAlert {
              appDownload {
                numberOfTimesSent
              }
            }
            lastLocationData {
              city
              coordinates {
                latitude
                longitude
              }
              countryCode
              locationInfo
              postalCode
              stateCode
              timestamp
              nextStopDetails {
                distanceToNextStop {
                  unit
                  value
                }
                etaToNextStop {
                  date
                  time
                  timezone
                }
                sequence
                stopExternalID
                stopID
                stopType
              }
            }
            appDownloadActions
          }
          source
          sourceDisplayName
          sourceID
          sourceType
        }
        ... on SFTPSource {
          addedAt
          filterReason
          isActive
          lastLocationFetchedAt
          locations {
            city
            coordinates {
              latitude
              longitude
            }
            countryCode
            locationInfo
            postalCode
            stateCode
            timestamp
          }
          metadata {
            fileType
            sender {
              dot
              mcNumber
              sftpUsername
              scac
              truckNumber
              trailerNumber
            }
            lastLocationData {
              city
              coordinates {
                latitude
                longitude
              }
              countryCode
              locationInfo
              postalCode
              stateCode
              timestamp
              nextStopDetails {
                distanceToNextStop {
                  unit
                  value
                }
                etaToNextStop {
                  date
                  time
                  timezone
                }
                sequence
                stopExternalID
                stopID
                stopType
              }
            }
          }
          source
          sourceDisplayName
          sourceID
          sourceType
        }
        ... on TiveDeviceSource {
          isActive
          addedAt
          filterReason
          lastLocationFetchedAt
          locations {
            city
            coordinates {
              latitude
              longitude
            }
            countryCode
            locationInfo
            postalCode
            stateCode
            timestamp
          }
          metadata {
            errors {
              date
              code
              rawMessage
            }
            lastLocationData {
              city
              coordinates {
                latitude
                longitude
              }
              countryCode
              locationInfo
              postalCode
              stateCode
              timestamp
              nextStopDetails {
                distanceToNextStop {
                  unit
                  value
                }
                etaToNextStop {
                  date
                  time
                  timezone
                }
                sequence
                stopExternalID
                stopID
                stopType
              }
            }
            provider
            tiveDeviceID
          }
          source
          sourceDisplayName
          sourceID
          sourceType
        }

        ... on ELDAppSource {
          isActive
          addedAt
          filterReason
          lastLocationFetchedAt
          locations {
            city
            coordinates {
              latitude
              longitude
            }
            countryCode
            locationInfo
            postalCode
            stateCode
            timestamp
          }
          metadata {
            errors {
              date
              code
              rawMessage
            }
            lastLocationData {
              city
              coordinates {
                latitude
                longitude
              }
              countryCode
              locationInfo
              postalCode
              stateCode
              timestamp
              nextStopDetails {
                distanceToNextStop {
                  unit
                  value
                }
                etaToNextStop {
                  date
                  time
                  timezone
                }
                sequence
                stopExternalID
                stopID
                stopType
              }
            }
            provider
            deviceID
          }
          source
          sourceID
        }

        ... on ExternalTrackingLinkSource {
          addedAt
          filterReason
          isActive
          lastLocationFetchedAt
          locations {
            city
            coordinates {
              latitude
              longitude
            }
            countryCode
            locationInfo
            postalCode
            stateCode
            timestamp
          }
          metadata {
            errors {
              date
              code
              rawMessage
            }
            isSupported
            lastLocationData {
              city
              coordinates {
                latitude
                longitude
              }
              countryCode
              locationInfo
              postalCode
              stateCode
              timestamp
              nextStopDetails {
                distanceToNextStop {
                  unit
                  value
                }
                etaToNextStop {
                  date
                  time
                  timezone
                }
                sequence
                stopExternalID
                stopID
                stopType
              }
            }
            provider
            securityMessage
            url
          }
          source
          sourceID
        }

        ... on TMSAppSource {
          isActive
          addedAt
          filterReason
          lastLocationFetchedAt
          locations {
            city
            coordinates {
              latitude
              longitude
            }
            countryCode
            locationInfo
            postalCode
            stateCode
            timestamp
          }
          metadata {
            sourceID
            lastLocationData {
              city
              coordinates {
                latitude
                longitude
              }
              countryCode
              locationInfo
              postalCode
              stateCode
              timestamp
              nextStopDetails {
                distanceToNextStop {
                  unit
                  value
                }
                etaToNextStop {
                  date
                  time
                  timezone
                }
                sequence
                stopExternalID
                stopID
                stopType
              }
            }
          }
          source
          sourceDisplayName
          sourceID
          sourceType
        }
        ... on VisibilityAppSource {
          isActive
          addedAt
          filterReason
          lastLocationFetchedAt
          locations {
            city
            coordinates {
              latitude
              longitude
            }
            countryCode
            locationInfo
            postalCode
            stateCode
            timestamp
          }
          metadata {
            deviceID
            provider
            sourceID
            lastLocationData {
              city
              coordinates {
                latitude
                longitude
              }
              countryCode
              locationInfo
              postalCode
              stateCode
              timestamp
              nextStopDetails {
                distanceToNextStop {
                  unit
                  value
                }
                etaToNextStop {
                  date
                  time
                  timezone
                }
                sequence
                stopExternalID
                stopID
                stopType
              }
            }
          }
          source
          sourceDisplayName
          sourceID
          sourceType
        }
      }
      lastLocation {
        coordinates {
          latitude
          longitude
        }
        timestamp
      }
    }
  }
`

export const UPDATE_SHIPMENT_TRACKING_WINDOW = gql`
  mutation UpdateShipmentTrackingWindow($input: UpdateShipmentTrackingWindowInput!) {
    updateShipmentTrackingWindow(input: $input) {
      id
    }
  }
`

export const SHIPMENT_TRACKING_DATA = gql`
  query ShipmentTrackingData($shipmentID: ID!) {
    shipmentTrackingData(input: { shipmentID: $shipmentID }) {
      id
      trackingStartTime
      trackingEndTime
    }
  }
`

export const PUBLIC_SHIPMENT_TRACKING_SOURCES = gql`
  query PublicTrackingSources($shipmentID: ID!) {
    publicTrackingSources(input: { shipmentID: $shipmentID }) {
      data {
        __typename
        ... on APISource {
          isActive
          addedAt
          filterReason
          lastLocationFetchedAt
          locations {
            city
            coordinates {
              latitude
              longitude
            }
            countryCode
            locationInfo
            postalCode
            stateCode
            timestamp
          }
          metadata {
            sourceID
            lastLocationData {
              city
              coordinates {
                latitude
                longitude
              }
              countryCode
              locationInfo
              postalCode
              stateCode
              timestamp
              nextStopDetails {
                distanceToNextStop {
                  unit
                  value
                }
                etaToNextStop {
                  date
                  time
                  timezone
                }
                sequence
                stopExternalID
                stopID
                stopType
              }
            }
          }
          source
          sourceDisplayName
          sourceID
          sourceType
        }
        ... on ExternalTrackingLinkSource {
          addedAt
          filterReason
          isActive
          lastLocationFetchedAt
          locations {
            city
            coordinates {
              latitude
              longitude
            }
            countryCode
            locationInfo
            postalCode
            stateCode
            timestamp
          }
          metadata {
            errors {
              date
              code
              rawMessage
            }
            isSupported
            lastLocationData {
              city
              coordinates {
                latitude
                longitude
              }
              countryCode
              locationInfo
              postalCode
              stateCode
              timestamp
              nextStopDetails {
                distanceToNextStop {
                  unit
                  value
                }
                etaToNextStop {
                  date
                  time
                  timezone
                }
                sequence
                stopExternalID
                stopID
                stopType
              }
            }
            provider
            url
          }
          source
          sourceID
        }
        ... on MobileAppSource {
          isActive
          locations {
            city
            coordinates {
              latitude
              longitude
            }
            countryCode
            locationInfo
            postalCode
            stateCode
            timestamp
          }
          metadata {
            deviceID
            deviceInfo {
              androidSDKVersion
              deviceID
              batteryLevel
              deviceManufacturer
              deviceModel
              deviceName
              devicePlatform
              deviceOS
              deviceOSVersion
              isDeviceCharging
              isDeviceVirtual
              iOSVersion
              languageCode
            }
            hasSentMockLocations
            isHistoricalDevice
            isLoggedIn
            lastLogin
            lastLogout
            lastSeen
            locationSettings {
              accuracyAuthorization
              authorizationStatus
              locationServicesEnabled
              gpsGeolocationProviderAvailable
              networkGeolocationProviderAvailable
            }
            user {
              id
              name
              photo
              phone
            }
            lastLocationData {
              city
              coordinates {
                latitude
                longitude
              }
              countryCode
              locationInfo
              postalCode
              stateCode
              timestamp
              nextStopDetails {
                distanceToNextStop {
                  unit
                  value
                }
                etaToNextStop {
                  date
                  time
                  timezone
                }
                sequence
                stopExternalID
                stopID
                stopType
              }
            }
          }
          source
        }

        ... on TiveDeviceSource {
          isActive
          addedAt
          filterReason
          lastLocationFetchedAt
          locations {
            city
            coordinates {
              latitude
              longitude
            }
            countryCode
            locationInfo
            postalCode
            stateCode
            timestamp
          }
          metadata {
            errors {
              date
              code
              rawMessage
            }
            lastLocationData {
              city
              coordinates {
                latitude
                longitude
              }
              countryCode
              locationInfo
              postalCode
              stateCode
              timestamp
              nextStopDetails {
                distanceToNextStop {
                  unit
                  value
                }
                etaToNextStop {
                  date
                  time
                  timezone
                }
                sequence
                stopExternalID
                stopID
                stopType
              }
            }
            provider
            tiveDeviceID
          }
          source
          sourceDisplayName
          sourceID
          sourceType
        }
        ... on SFTPSource {
          addedAt
          filterReason
          isActive
          lastLocationFetchedAt
          locations {
            city
            coordinates {
              latitude
              longitude
            }
            countryCode
            locationInfo
            postalCode
            stateCode
            timestamp
          }
          metadata {
            fileType
            sender {
              dot
              mcNumber
              sftpUsername
              scac
              truckNumber
              trailerNumber
            }
            lastLocationData {
              city
              coordinates {
                latitude
                longitude
              }
              countryCode
              locationInfo
              postalCode
              stateCode
              timestamp
              nextStopDetails {
                distanceToNextStop {
                  unit
                  value
                }
                etaToNextStop {
                  date
                  time
                  timezone
                }
                sequence
                stopExternalID
                stopID
                stopType
              }
            }
          }
          source
          sourceDisplayName
          sourceID
          sourceType
        }
        ... on ELDAppSource {
          isActive
          addedAt
          filterReason
          lastLocationFetchedAt
          locations {
            city
            coordinates {
              latitude
              longitude
            }
            countryCode
            locationInfo
            postalCode
            stateCode
            timestamp
          }
          metadata {
            errors {
              date
              code
              rawMessage
            }
            lastLocationData {
              city
              coordinates {
                latitude
                longitude
              }
              countryCode
              locationInfo
              postalCode
              stateCode
              timestamp
              nextStopDetails {
                distanceToNextStop {
                  unit
                  value
                }
                etaToNextStop {
                  date
                  time
                  timezone
                }
                sequence
                stopExternalID
                stopID
                stopType
              }
            }
            provider
            deviceID
          }
          source
          sourceID
        }
        ... on VisibilityAppSource {
          isActive
          addedAt
          filterReason
          lastLocationFetchedAt
          locations {
            city
            coordinates {
              latitude
              longitude
            }
            countryCode
            locationInfo
            postalCode
            stateCode
            timestamp
          }
          metadata {
            deviceID
            errors {
              date
              code
              rawMessage
            }
            provider
            sourceID
            lastLocationData {
              city
              coordinates {
                latitude
                longitude
              }
              countryCode
              locationInfo
              postalCode
              stateCode
              timestamp
              nextStopDetails {
                distanceToNextStop {
                  unit
                  value
                }
                etaToNextStop {
                  date
                  time
                  timezone
                }
                sequence
                stopExternalID
                stopID
                stopType
              }
            }
          }
          source
          sourceDisplayName
          sourceID
          sourceType
        }
      }
      id
      lastLocation {
        coordinates {
          latitude
          longitude
        }
        city
        state
        timestamp
      }
      referenceNumbers {
        billOfLading
        orderNumber
        progressiveRotatingOrderNumber
        purchaseOrderNumber
      }
      shipmentNumber
      shipmentStatus
      stops {
        appointment {
          start {
            date
            time
            timezone
          }
          end {
            date
            time
            timezone
          }
          isFirstComeFirstServe
        }
        arrivalDateTime {
          date
          time
        }
        departureDateTime {
          date
          time
        }
        eta {
          date
          time
        }
        sequence
        type
        warehouse {
          address {
            addressLabel
            boundingBox
            city
            coordinates {
              latitude
              longitude
            }
            country
            county
            postalCode
            state
            stateCode
            street
            streetNumber
          }
        }
      }
    }
  }
`
export interface ICoordinates {
  latitude: number
  longitude: number
}

export type TShipmentTrackingReturn = {
  created: string
  data: TShipmentTrackingSourceTypes[]
  destination?: IStop
  id: string
  lastLocation: { city?: string; coordinates: ICoordinates; state?: string; timestamp: string } | null
  modified: string
  origin?: IStop
  referenceNumbers: IReferenceNumbers | null
  shipmentMode: string | null
  shipmentNumber: string
  shipmentStatus: ShipmentStatusEnum
  stops: IStop[]
  trackingMapURL?: string
}
